<template>
  <div>
    <v-row row wrap>
      <v-col>
        <v-btn
          color="primary"
          outlined
          small
          @click="handleClickCloseLibrary"
          class="pull-left mr-5"
        >
          Back
        </v-btn>
        <span class="details-heading application-heading">
          Search Library
        </span>
        <v-icon
          size="18"
          title="Close panel"
          class="mr-2 pull-right"
          @click="handleClickClose"
          >fas fa-times</v-icon
        >
      </v-col>
    </v-row>

    <v-row
      v-if="isLibraryFiltersFeatureUnlocked"
      dense
      class="pa-0 ma-0 search-library-filters-section"
    >
      <v-col cols="2">
        <v-text-field
          append-icon="mdi-magnify"
          label="Search"
          v-model="filterKeywords"
          single-line
          hide-details
          clearable
          class="px-3 search-library-filter-keywords"
          @input="debounceFilterKeywords"
        >
        </v-text-field>
      </v-col>

      <v-col cols="2">
        <v-select
          :items="categories"
          v-model="filterCategories"
          label="Select categories"
          multiple
          single-line
          hide-details
          @change="handleFilterCategories"
        >
          <template v-slot:selection="{ item, index }">
            <span v-if="index === 0">
              {{ filterCategories.length }} Categories selected
            </span>
          </template>
        </v-select>
      </v-col>

      <v-col cols="2">
        <AssetDateRange
          title="Last Updated"
          :items="timeRangeOptions"
          v-model="activeTimeRange"
          :customRange="customDateRange"
          @change="handleDateRangeChange"
          default-content
          class="pull-left"
        ></AssetDateRange>
      </v-col>
    </v-row>

    <v-row row wrap>
      <v-col>
        <Loader v-if="loading" text=""></Loader>
        <div v-if="!loading && !searchLibrary.length">No searches found.</div>
        <div v-if="!loading && searchLibrary.length">
          <v-data-iterator
            hide-default-footer
            :items="searchLibrary"
            :items-per-page.sync="itemsPerPage"
            :page.sync="page"
            :search="filterKeywords"
            :custom-filter="handleCustomFilters"
          >
            <template v-slot:no-results>
              <div class="py-5 ml-5">
                No searches match. Please check your filters.
              </div>
            </template>
            <template v-slot:default="{ items }">
              <div class="search-library-list">
                <v-card
                  flat
                  class="my-2 pa-2"
                  v-for="(item, index) in items"
                  :key="index"
                >
                  <div class="search-library-item">
                    <div>
                      <div v-html="item.name"></div>
                      <div class="caption" v-html="item.description"></div>
                    </div>
                    <div>
                      <v-chip
                        v-for="category in categoriesFormatted(item)"
                        :key="category"
                        small
                        color="primary"
                        class="mr-2"
                        >{{ category }}</v-chip
                      >
                    </div>
                    <div>{{ dateFormatted(item.updatedAt) }}</div>
                    <div>
                      <v-btn
                        color="primary"
                        outlined
                        small
                        @click="viewSearchResults(item)"
                        class="pull-right ml-2"
                      >
                        View Results
                      </v-btn>
                    </div>
                  </div>
                </v-card>
              </div>
            </template>

            <!-- Custom Footer -->
            <template v-slot:footer>
              <v-row class="mt-2 mx-2" align="center" justify="center">
                <span class="grey--text">Items per page</span>
                <v-menu offset-y>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      dark
                      text
                      color="primary"
                      class="ml-2"
                      v-bind="attrs"
                      v-on="on"
                    >
                      {{ itemsPerPage }}
                      <v-icon>mdi-chevron-down</v-icon>
                    </v-btn>
                  </template>
                  <v-list>
                    <v-list-item
                      v-for="(number, index) in itemsPerPageArray"
                      :key="index"
                      @click="updateItemsPerPage(number)"
                    >
                      <v-list-item-title>{{ number }}</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>

                <v-spacer></v-spacer>

                <span class="mr-4 grey--text"> Page {{ page }} </span>
                <v-btn
                  :disabled="page <= 1 || loading"
                  fab
                  x-small
                  color="primary"
                  class="mr-1"
                  @click="formerPage"
                >
                  <v-icon>mdi-chevron-left</v-icon>
                </v-btn>
                <v-btn
                  :disabled="!showNext || loading"
                  fab
                  x-small
                  color="primary"
                  class="ml-1"
                  @click="nextPage"
                >
                  <v-icon>mdi-chevron-right</v-icon>
                </v-btn>
              </v-row>
            </template>
          </v-data-iterator>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import lodashCloneDeep from "lodash/cloneDeep";
import lodashDebounce from "lodash/debounce";
import lodashSortBy from "lodash/sortBy";
import lodashUniq from "lodash/uniq";

import { mapGetters } from "vuex";
import { MODULE_PREFIX_USER } from "@/store";
import { SAVE_PREFERENCES } from "@/store/actions.type";
import { getSearchLibrary } from "@/services/search/searchLibrary.service";
import { formatSavedSearch } from "@/services/search/savedSearch.service";
import {
  getFormatted,
  getTodayStartEndDate,
  DATE_FORMAT,
  getRelativeDate,
} from "@/common/date.utilities";
import { SEARCH_LIBRARY_TIME_OPTIONS } from "@/common/constants/savedSearch";
import { MODULE_PREFIX_SAVED_SEARCH } from "@/store";
import { GET_SAVED_SEARCH_DETAILS } from "@/store/actions.type";

const EMITTED_ACTIONS = {
  CLOSE_LIBRARY: "close-library",
  CLICK_CLOSE: "click-close",
  VIEW_SEARCH_RESULTS: "view-search-results",
};

export default {
  name: "SearchLibrary",
  props: {},
  components: {
    AssetDateRange: () => import("@/components/assetManagement/AssetDateRange"),
    Loader: () => import("@/components/common/Loader"),
  },
  data() {
    const initialDateStart = getTodayStartEndDate("2024-01-01").start;
    const initialDateEnd = getTodayStartEndDate().end;

    const timeOptions = lodashCloneDeep(SEARCH_LIBRARY_TIME_OPTIONS);
    timeOptions.push({
      value: {
        label: "Since Jan 1, 2024",
        isRelative: false,
        start: initialDateStart,
        end: initialDateEnd,
      },
    });

    return {
      loading: false,
      itemsPerPageArray: [10, 20, 40, 80, 100, 250],
      page: 1,
      itemsPerPage: 100,
      searchLibrary: [],
      filterKeywords: "",
      filterCategories: null,
      filterDateRange: {
        start: initialDateStart.split("T")[0],
        end: initialDateEnd.split("T")[0],
      },
      timeRangeOptions: timeOptions,
      activeTimeRange: timeOptions[timeOptions.length - 1].value,
      categories: [],
    };
  },
  async mounted() {
    this.updateFromFilters();
    await this.getSearches();
  },
  computed: {
    ...mapGetters({
      preferences: MODULE_PREFIX_USER + "preferences",
    }),
    isLibraryFiltersFeatureUnlocked() {
      return this.featureUnlocked("saved-search-library-filters");
    },
    numberOfPages() {
      return Math.ceil(this.searchLibrary.length / this.itemsPerPage);
    },
    showNext() {
      return this.page + 1 <= this.numberOfPages;
    },
    customDateRange() {
      return this.activeTimeRange?.useDatePicker ? this.filterDateRange : null;
    },
  },
  methods: {
    handleClickCloseLibrary() {
      this.$emit(EMITTED_ACTIONS.CLOSE_LIBRARY, true);
    },
    handleClickClose() {
      this.$emit(EMITTED_ACTIONS.CLICK_CLOSE, true);
    },
    viewSearchResults(search) {
      this.$store.dispatch(
        MODULE_PREFIX_SAVED_SEARCH + GET_SAVED_SEARCH_DETAILS,
        formatSavedSearch(search)
      );
      this.$emit(EMITTED_ACTIONS.VIEW_SEARCH_RESULTS, search);
    },
    reloadLibrary() {
      this.searchLibrary = [];
      this.page = 1;
      this.getSearches();
    },
    async getSearches() {
      this.loading = true;
      this.searchLibrary = await getSearchLibrary();
      this.getCategories();
      this.loading = false;
    },
    getCategories() {
      let categories = [];
      if (this.searchLibrary?.length) {
        this.searchLibrary.forEach((search) => {
          if (search?.categories) {
            categories = [...categories, ...search.categories];
          }
        });
      }
      this.categories = lodashSortBy(lodashUniq(categories));
    },
    handleCustomFilters(items, search) {
      const categories = this.filterCategories?.length && this.filterCategories;
      const startDate =
        this.filterDateRange?.start &&
        new Date(getTodayStartEndDate(this.filterDateRange.start).start);
      const endDate =
        this.filterDateRange?.end &&
        new Date(getTodayStartEndDate(this.filterDateRange.end).end);

      return items.filter((item) => {
        const matchesKeywords = (() => {
          if (!search) {
            return true;
          }
          const regex = new RegExp(search, "i");
          return regex.test(item.name) || regex.test(item.description);
        })();

        const matchesCategories = (() => {
          if (!categories?.length) {
            return true;
          }
          return categories.some(
            (cat) => item.categories && item.categories.includes(cat)
          );
        })();

        const matchesDateRange = (() => {
          if (!startDate || !endDate) {
            return true;
          }
          const itemDate = new Date(item.updatedAt);
          return itemDate >= startDate && itemDate <= endDate;
        })();
        return matchesKeywords && matchesCategories && matchesDateRange;
      });
    },
    nextPage() {
      this.page += 1;
    },
    formerPage() {
      if (this.page - 1 >= 1) {
        this.page -= 1;
      }
    },
    updateItemsPerPage(number) {
      if (number > this.itemsPerPage) {
        this.itemsPerPage = number;
        this.reloadLibrary();
      } else {
        this.itemsPerPage = number;
      }
    },
    categoriesFormatted(item) {
      if (item?.categories?.length) {
        return lodashSortBy(lodashUniq(item.categories));
      }
    },
    dateFormatted(date) {
      return getFormatted(date);
    },
    toFormattedDateRange({ isRelative, start, end }) {
      return {
        start: getRelativeDate(isRelative, start).format(DATE_FORMAT),
        end: getRelativeDate(isRelative, end).format(DATE_FORMAT),
      };
    },
    async handleFilterCategories() {
      await this.savePrefs();
    },
    debounceFilterKeywords: lodashDebounce(async () => {
      await this.savePrefs();
    }, 750),
    updateFromFilters() {
      let filterDateRange = null;
      const { keywords, categories, dateRange, activeTimeRange } =
        this.preferences?.searchLibrary?.filters || {};

      if (activeTimeRange) {
        this.activeTimeRange = activeTimeRange;
      }

      // if the user have already define an active date for its preferences and is not a custom date, take that range.
      if (activeTimeRange && activeTimeRange.isRelative === true) {
        filterDateRange = this.toFormattedDateRange(activeTimeRange);
      }

      // if the user hasn't defined any preferences for an activeTimeRange and dateRange yet,
      // set the date range to its default last 30 days value.
      if (!dateRange && this.activeTimeRange) {
        filterDateRange = this.toFormattedDateRange(this.activeTimeRange);
      }

      this.filterKeywords = keywords;
      this.filterCategories = categories;
      this.filterDateRange = filterDateRange ?? dateRange;
    },
    async handleDateRangeChange(dates) {
      this.filterDateRange = {
        start: dates.range.start.format(DATE_FORMAT),
        end: dates.range.end.format(DATE_FORMAT),
      };
      this.activeTimeRange = dates.activeItem;
      await this.savePrefs();
    },
    async savePrefs() {
      let preferences = lodashCloneDeep(this.preferences);
      preferences.searchLibrary = preferences.searchLibrary || {};
      preferences.searchLibrary.filters =
        preferences.searchLibrary.filters || {};
      preferences.searchLibrary.filters.activeTimeRange = this.activeTimeRange;
      preferences.searchLibrary.filters.dateRange = this.filterDateRange;
      preferences.searchLibrary.filters.keywords = this.filterKeywords;
      preferences.searchLibrary.filters.categories = this.filterCategories;
      await this.$store.dispatch(
        MODULE_PREFIX_USER + SAVE_PREFERENCES,
        preferences
      );
      preferences = null;
    },
  },
  watch: {
    "preferences.searchLibrary": {
      handler() {
        this.updateFromFilters();
      },
      deep: true,
      immediate: true,
    },
  },
};
</script>
<style scoped>
.details-heading {
  font-size: 20px;
}
.search-library-list {
  max-height: calc(100vh - 185px);
  overflow-y: auto;
}
.search-library-item {
  display: grid;
  grid-template-columns: 1fr 300px 250px 100px;
  grid-gap: 1rem;
}
.search-library-filters-section {
  display: flex;
}
.search-library-filters-section .col {
  max-width: fit-content;
}
.search-library-filters-section .search-library-filter-keywords {
  min-width: 200px;
}
</style>
